import { coerceNumberProperty } from '@angular/cdk/coercion';
import { MatSort, Sort } from '@angular/material/sort';
import moment from 'moment';
import { EMPTY } from 'rxjs';
import { transformerFactory } from './transform';

const ISO_8601 =
  /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|(?:(?:\+|-)\d{2}:\d{2}))?)?$/;

export function sortingDataAccessor<T>(
  row: T,
  id: string,
  valueAccessor: (row: T, path: string) => any
): string | number {
  const value = valueAccessor(row, id);

  return coerceNumberProperty(value, value);
}

export function sortData<T>(
  data: T[],
  sort: MatSort,
  valueAccessor: (row: T, path: string) => any
): T[] {
  if (!sort) {
    return data;
  }

  const { active, direction } = sort;

  if (!active || direction === '') {
    return data;
  }

  return data.slice().sort((a, b) => {
    const valueA = sortingDataAccessor(a, active, valueAccessor);
    const valueB = sortingDataAccessor(b, active, valueAccessor);
    let comparatorResult = 0;

    /**
     * If both valueA and valueB exist (truthy), then compare the two. Otherwise, check if
     * one value exists while the other doesn't. In this case, existing value should come first.
     * This avoids inconsistent results when comparing values to undefined/null.
     * If neither value exists, return 0 (equal).
     */
    if (valueA && valueB) {
      if (ISO_8601.test(valueA + '') && ISO_8601.test(valueB + '')) {
        comparatorResult = moment(valueA).isAfter(valueB) ? 1 : -1;
      } else if (valueA > valueB) {
        comparatorResult = 1;
      } else if (valueA < valueB) {
        comparatorResult = -1;
      }
    } else if (valueA) {
      comparatorResult = 1;
    } else if (valueB) {
      comparatorResult = -1;
    }

    return comparatorResult * (direction === 'asc' ? 1 : -1);
  });
}

/**
 * Sort data be {@link MatSort} config.
 *
 * @param sort Instance of sort to get events from.
 */
export function sort<T>(
  sort: MatSort,
  valueAccessor: (row: T, path: string) => any
) {
  return transformerFactory<T, Sort>(sort ? sort.sortChange : EMPTY, ([data]) =>
    sortData<T>(data, sort, valueAccessor)
  );
}
